import { Box, Button, Grid, Text } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import {
  DashboardIcon,
  LeftArrowIcon,
  RanaCard,
  RanaModal,
  RanaProjectSelect,
  RanaTable,
  SystemStatusPage,
  hasRanaTenant,
  retrieveTenantAndOrganisation,
} from "@nens/ranalib";
import { useLocalStorage } from "@nens/react/hooks";
import * as api from "@nens/threedi/openapi-client";

import { getV3Api } from "../apiConfiguration";
import { APP_BASE_HREF, AppDispatch } from "../App";
import { RanaLiveIcon } from "../components/rana-live-logo";
import { ActiveSimulation, getActiveSimulations } from "../state/activeSimulations";
import { startWithSimulation } from "../state/appState";
import { allOrganisations } from "../state/session";
import { checkPermissionForRole } from "../utils/authorizationUtils";

import SelectOrganisation from "./../components/SelectOrganisation";
import {
  getSelectedOrganisation,
  setSelectedOrganisation,
} from "./../state/selectedStartScreenOptions";
import { getUser } from "./../state/session";
import activeSimulationImageSrc from "./activeSimulation.svg?url";
import newSimulationImageSrc from "./newSimulation.svg?url";

export type SelectedModel = null | api.ThreediModel;
export type SelectedSimulation = null | ActiveSimulation;
export type SelectedOrganisation = null | string;
const NO_CHOICE_MADE = "No choice made";
const NEW_SESSION = "New session";
const FOLLOW_SESSION = "Follow session";
const SCENARIOS_PER_PAGE = 5;

const MIN_MODAL_WIDTH = "1200px";
const MIN_MODAL_HEIGHT = "600px";

// Fetch simulations for the selected model
const useOrganisationForSelectedModelQuery = (selected: SelectedModel) => {
  const v3Api = getV3Api();
  return useQuery({
    queryKey: ["selected-model", selected],
    enabled: !!selected,
    retry: true,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!selected) return;
      const simulations = await v3Api.simulationsList({
        threedimodelId: selected.id,
      });
      return simulations.results[0]?.organisation;
    },
  });
};

// Fetch permissions for the selected organisation and user
const useOrganisationPermissionsQuery = (
  user: api.User | null,
  selectedOrganisation: string | null,
  organisations: api.Organisation[]
) => {
  return useQuery({
    queryKey: [
      "organisation-permissions",
      user?.username,
      selectedOrganisation,
      organisations.map((o) => o.uniqueId).join(""),
    ],
    enabled: !!user && !!selectedOrganisation && organisations.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!user || !selectedOrganisation) throw Error("Never happens");
      const selectedOrganisationName = organisations.find(
        (item) => item.uniqueId === selectedOrganisation
      )?.name;
      if (!selectedOrganisationName) return { isViewer: false, isSimulationRunner: false };

      const [superUser, viewer, simulationRunner] = await Promise.all([
        checkPermissionForRole(
          user.username,
          selectedOrganisationName,
          selectedOrganisation,
          "superuser"
        ),
        checkPermissionForRole(
          user.username,
          selectedOrganisationName,
          selectedOrganisation,
          "viewer"
        ),
        checkPermissionForRole(
          user.username,
          selectedOrganisationName,
          selectedOrganisation,
          "simulation_runner"
        ),
      ]);
      return {
        isViewer: superUser || viewer,
        isSimulationRunner: superUser || simulationRunner,
      };
    },
  });
};

function SelectScenario() {
  const navigate = useNavigate();
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();

  const activeSimulations = useSelector(getActiveSimulations);
  const [modelsAreLoading, setModelsAreLoading] = useState(false);
  const [pageModel, setPageModel] = useState(1);
  const [pageSimulation, setPageSimulation] = useState(1);
  const [totalPagesModel, setTotalPagesModel] = useState(1);
  const [totalPagesSimulation, setTotalPagesSimulation] = useState(1);
  const [searchStringModel, _setSearchStringModel] = useLocalStorage<string>(
    "selectScenarioSearch",
    ""
  );
  const setSearchStringModel: typeof _setSearchStringModel = (value) => {
    setPageModel(1);
    _setSearchStringModel(value);
  };
  const [searchStringSimulation, _setSearchStringSimulation] = useLocalStorage<string>(
    "selectScenarioSimulation",
    ""
  );
  const setSearchStringSimulation: typeof _setSearchStringSimulation = (value) => {
    setPageSimulation(1);
    _setSearchStringSimulation(value);
  };
  const [selectedModel, setSelectedModel] = useState<SelectedModel>(null);
  const [selectedSimulation, setSelectedSimulation] = useState<SelectedSimulation>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  const selectedOrganisation = useSelector(getSelectedOrganisation);

  const [selectedTab, setSelectedTab] = useState<
    typeof NO_CHOICE_MADE | typeof NEW_SESSION | typeof FOLLOW_SESSION
  >(NO_CHOICE_MADE);

  const [threediModels, setThreediModels] = useState<api.ThreediModel[]>([]);
  const [simulations, setSimulations] = useState<ActiveSimulation[]>([]);

  const user = useSelector(getUser);

  const organisations = useSelector(allOrganisations);
  const hasMultipleOrganisations = organisations.length > 1;

  const {
    data: selectedOrganisationFromModel,
    isSuccess: fetchedSelectedOrganisationFromModel,
    isLoading: fetchingSelectedOrganisationFromModel,
  } = useOrganisationForSelectedModelQuery(hasMultipleOrganisations ? selectedModel : null);

  useEffect(() => {
    if (fetchedSelectedOrganisationFromModel) {
      dispatch(setSelectedOrganisation(selectedOrganisationFromModel ?? null));
    }
  }, [dispatch, fetchedSelectedOrganisationFromModel, selectedOrganisationFromModel]);

  const selectedOrgBySimulation = selectedSimulation
    ? organisations.find((org) => org.name === selectedSimulation.organisation_name)?.uniqueId
    : null;

  if (selectedOrgBySimulation && selectedOrgBySimulation !== selectedOrganisation) {
    dispatch(setSelectedOrganisation(selectedOrgBySimulation));
  }

  const { data: permissions, isLoading: fetchingPermissions } = useOrganisationPermissionsQuery(
    user,
    selectedOrganisation,
    organisations
  );

  const userIsViewer = permissions?.isViewer || false;
  const userIsSimulationRunner = permissions?.isSimulationRunner || false;

  useEffect(() => {
    const v3Api = getV3Api();
    setModelsAreLoading(true);
    v3Api
      .threedimodelsList({
        limit: SCENARIOS_PER_PAGE,
        offset: SCENARIOS_PER_PAGE * (pageModel - 1),
        nameIcontains: searchStringModel,
        ordering: "revision_commit_date",
        // If there is no selectedProjectId then we want to filter on projectId=null, which has
        // a slightly different syntax, ranaProjectId: null would filter on the *string* "null".
        ...(selectedProjectId
          ? { revisionSchematisationRanaProjectId: selectedProjectId }
          : { revisionSchematisationRanaProjectIdIsnull: true }),
        /* Models created after 3Di Klondike (2019) have inpyVersionSlug "-",
           older models cannot run on the current system. */
        inpyVersionSlug: "-",
      })
      .then((response) => {
        setModelsAreLoading(false);
        setThreediModels(response.results);
        if (response.count > 0) {
          setTotalPagesModel(Math.ceil(response.count / SCENARIOS_PER_PAGE));
        } else {
          setTotalPagesModel(1);
        }
      });
  }, [pageModel, searchStringModel, selectedProjectId]);

  useEffect(() => {
    const simulationsList = Object.values(activeSimulations).filter((simulation) => {
      // Filter through the list of active simulations by entered search term
      return simulation.name.toLowerCase().includes(searchStringSimulation.toLowerCase());
    });
    setSimulations(
      simulationsList.filter((_, index) => {
        // Paginate the filtered list
        return (
          index >= SCENARIOS_PER_PAGE * (pageSimulation - 1) &&
          index < SCENARIOS_PER_PAGE * pageSimulation
        );
      })
    );
    if (simulationsList.length > 0) {
      setTotalPagesSimulation(Math.ceil(simulationsList.length / SCENARIOS_PER_PAGE));
    } else {
      setTotalPagesSimulation(1);
    }
  }, [activeSimulations, pageSimulation, searchStringSimulation]);

  const clickHandlerSetSelectedOrganisationAndThreediModel = useCallback(() => {
    if (selectedOrganisation && selectedModel && selectedTab === NEW_SESSION) {
      const selectedOrganisationObj = organisations.find(
        (item) => item.uniqueId === selectedOrganisation
      );
      if (selectedOrganisationObj) {
        navigate(
          `${APP_BASE_HREF}/quickstart/model-and-organisation/${selectedModel.id}/${selectedOrganisation}/`
        );
      }
    }
  }, [selectedOrganisation, selectedModel, selectedTab, navigate, organisations]);

  const clickHandlerStartWithSimulation = () => {
    if (selectedSimulation && selectedTab === FOLLOW_SESSION) {
      const v3Api = getV3Api();
      v3Api
        .simulationsRead({
          id: selectedSimulation.uid,
        })
        .then((simulation) => {
          dispatch(startWithSimulation(simulation));
        });
    }
  };

  const getStartButtonTitle = () => {
    if (!selectedOrganisation) {
      return intl.formatMessage({
        id: "select_model.button_start_tooltip_no_organisation",
        defaultMessage: "Select an organisation to start a simulation",
      });
    }
    if (fetchingPermissions) {
      return intl.formatMessage({
        id: "select_model.button_permissions_loading",
        defaultMessage: "Loading permissions...",
      });
    }
    if (!userIsSimulationRunner) {
      return intl.formatMessage({
        id: "select_model.button_start_missing_role",
        defaultMessage: "You need the role Simulation runner to be able to start simulations",
      });
    }
    if (selectedModel) {
      return `${intl.formatMessage({
        id: "select_model.button_start_tooltip_allowed",
        defaultMessage: "Create Rana simulation with selected model",
      })} "${selectedModel.name}"`;
    }
    return intl.formatMessage({
      id: "select_model.button_start_tooltip_not_allowed",
      defaultMessage: "First select a model in the list above",
    });
  };

  const getFollowButtonTitle = () => {
    if (!selectedOrganisation) {
      return intl.formatMessage({
        id: "select_model.button_follow_tooltip_no_organisation",
        defaultMessage: "Select an organisation to follow a simulation",
      });
    }
    if (fetchingPermissions) {
      return intl.formatMessage({
        id: "select_model.button_permissions_loading",
        defaultMessage: "Loading permissions...",
      });
    }
    if (!userIsViewer) {
      return intl.formatMessage({
        id: "select_model.button_follow_missing_role",
        defaultMessage: "You need the role viewer to be able to view simulations",
      });
    }
    if (selectedSimulation) {
      return `${intl.formatMessage({
        id: "select_model.button_follow_tooltip_allowed",
        defaultMessage: "Follow selected Rana simulation",
      })} "${selectedSimulation.name}"`;
    }
    return intl.formatMessage({
      id: "select_model.button_follow_tooltip_not_allowed",
      defaultMessage: "Select a simulation in the list above before starting",
    });
  };

  const renderModal = () => {
    if (selectedTab === NO_CHOICE_MADE) {
      return (
        <RanaModal
          minWidth={MIN_MODAL_WIDTH}
          minHeight={MIN_MODAL_HEIGHT}
          isOpen={true}
          blurBackground={false}
          topBar={<RanaLiveIcon width="235px" height="26px" />}
          buttons={[]}
        >
          <Box textAlign="right">
            <Button
              onClick={() => {
                if (hasRanaTenant()) {
                  window.location.assign(`/${retrieveTenantAndOrganisation().tenant}/`);
                } else {
                  window.location.assign("/login");
                }
              }}
              variant="unstyled"
              color="brand.analytic"
              mb="5px"
              _hover={{ color: "brand.deep_sea" }}
              cursor="pointer"
            >
              <DashboardIcon boxSize={5} mr={2} />
              Rana dashboard
            </Button>
            <Grid templateColumns="repeat(2, 1fr)" gap="12px" mb="12px">
              <RanaCard
                gridItemHeight="320px"
                title={intl.formatMessage({
                  id: "select_model.card_new_session_title",
                  defaultMessage: "Start a new Rana simulation",
                })}
                shortDescription={intl.formatMessage({
                  id: "select_model.card_new_session_description",
                  defaultMessage:
                    "Create a new Rana simulation by selecting a model and organisation of your choice.",
                })}
                buttonText={intl.formatMessage({
                  id: "select_model.card_new_session_button",
                  defaultMessage: "New session",
                })}
                imageSrc={newSimulationImageSrc}
                onClick={() => setSelectedTab(NEW_SESSION)}
              />
              <RanaCard
                gridItemHeight="320px"
                title={intl.formatMessage({
                  id: "select_model.card_follow_session_title",
                  defaultMessage: "Follow an active Rana simulation",
                })}
                shortDescription={intl.formatMessage({
                  id: "select_model.card_follow_session_description",
                  defaultMessage:
                    "Follow an active simulation that is already running on the Rana platform.",
                })}
                buttonText={intl.formatMessage({
                  id: "select_model.card_follow_session_button",
                  defaultMessage: "Follow session",
                })}
                imageSrc={activeSimulationImageSrc}
                onClick={() => setSelectedTab(FOLLOW_SESSION)}
              />
            </Grid>
          </Box>
        </RanaModal>
      );
    }

    if (selectedTab === NEW_SESSION) {
      return (
        <RanaModal
          minWidth={MIN_MODAL_WIDTH}
          minHeight={MIN_MODAL_HEIGHT}
          isOpen={true}
          blurBackground={false}
          topBar={<RanaLiveIcon width="235px" height="26px" />}
          buttons={[
            <Button
              variant="primary"
              key="start-button"
              onClick={clickHandlerSetSelectedOrganisationAndThreediModel}
              isDisabled={!(selectedModel && selectedOrganisation && userIsSimulationRunner)}
              title={getStartButtonTitle()}
            >
              <FormattedMessage id="select_model.button_start" defaultMessage="Start" />
            </Button>,
          ]}
        >
          <>
            <Button
              onClick={() => {
                setSelectedTab(NO_CHOICE_MADE);
              }}
              variant="unstyled"
              color="brand.analytic"
              mb="5px"
              _hover={{ color: "brand.deep_sea" }}
              cursor="pointer"
            >
              <LeftArrowIcon boxSize={5} mr={2} />
              Back
            </Button>
            <Box mb={4}>
              <RanaProjectSelect
                value={selectedProjectId}
                onChange={(projectId) => {
                  setSelectedProjectId(projectId);
                  setPageModel(1);
                  setSelectedModel(null);
                }}
              />
            </Box>
            <RanaTable
              rows={5}
              columnDefinitions={[
                {
                  width: "40%",
                  title: intl.formatMessage({
                    id: "select_model.table_header_model",
                    defaultMessage: "Model",
                  }),
                },
                {
                  width: "15%",
                  title: intl.formatMessage({
                    id: "select_model.table_header_revision",
                    defaultMessage: "Revision",
                  }),
                },
                {
                  width: "15%",
                  title: intl.formatMessage({
                    id: "select_model.table_header_date_updated",
                    defaultMessage: "Last updated",
                  }),
                },
                {
                  width: "25%",
                  title: intl.formatMessage({
                    id: "select_model.table_header_updated_by",
                    defaultMessage: "Updated by",
                  }),
                },
              ]}
              tableDataIds={threediModels.map((model) => "" + model.id)}
              emptyElement={
                <tr>
                  <td colSpan={4}>
                    <Text>No models to show</Text>
                  </td>
                </tr>
              }
              isLoading={modelsAreLoading}
            >
              <RanaTable.Filters
                search={{
                  w: 400,
                  searchString: searchStringModel,
                  setSearchString: setSearchStringModel,
                  placeholder: intl.formatMessage({
                    id: "select_model.search_model_placeholder_new_session",
                    defaultMessage: "Search and select a model to simulate",
                  }),
                }}
                checkboxes={[]}
              />
              <RanaTable.Container minH={(44 + 2) * 6}>
                <RanaTable.Header />
                <RanaTable.Body>
                  {threediModels.map((model) => (
                    <RanaTable.Row
                      key={"" + model.id}
                      rowId={"" + model.id}
                      onClick={() => setSelectedModel(model)}
                      isSelected={!!selectedModel && selectedModel.id === model.id}
                    >
                      <RanaTable.Cell>{model.name}</RanaTable.Cell>
                      <RanaTable.Cell>{model.revisionNumber || "---"}</RanaTable.Cell>
                      <RanaTable.Cell>
                        {model.revisionCommitDate
                          ? new Date(model.revisionCommitDate).toLocaleDateString()
                          : "---"}
                      </RanaTable.Cell>
                      <RanaTable.Cell>{model.user ? model.user : "---"}</RanaTable.Cell>
                    </RanaTable.Row>
                  ))}
                </RanaTable.Body>
              </RanaTable.Container>
              <RanaTable.Pagination
                limit={SCENARIOS_PER_PAGE}
                page={pageModel}
                numPages={totalPagesModel}
                setPage={setPageModel}
              />
            </RanaTable>
            {organisations.length > 1 ? (
              <div
                style={{
                  paddingTop: "5px",
                  marginTop: "10px",
                }}
              >
                <SelectOrganisation
                  organisations={organisations}
                  fetchingSelectedOrganisation={fetchingSelectedOrganisationFromModel}
                />
              </div>
            ) : null}
          </>
        </RanaModal>
      );
    }

    if (selectedTab === FOLLOW_SESSION) {
      return (
        <RanaModal
          minWidth={MIN_MODAL_WIDTH}
          minHeight={MIN_MODAL_HEIGHT}
          isOpen={true}
          blurBackground={false}
          topBar={<RanaLiveIcon width="235px" height="26px" />}
          buttons={[
            <Button
              isLoading={fetchingPermissions}
              variant="primary"
              key="follow-button"
              onClick={clickHandlerStartWithSimulation}
              isDisabled={fetchingPermissions || !selectedSimulation || !userIsViewer}
              title={getFollowButtonTitle()}
            >
              <FormattedMessage id="select_model.button_follow" defaultMessage="Follow" />
            </Button>,
          ]}
        >
          <>
            <Button
              onClick={() => {
                setSelectedTab(NO_CHOICE_MADE);
              }}
              variant="unstyled"
              color="brand.analytic"
              mb="5px"
              _hover={{ color: "brand.deep_sea" }}
              cursor="pointer"
            >
              <LeftArrowIcon boxSize={5} mr={2} />
              Back
            </Button>
            <RanaTable
              rows={5}
              columnDefinitions={[
                {
                  width: "40%",
                  title: intl.formatMessage({
                    id: "select_model.table_header_simulation",
                    defaultMessage: "Simulation",
                  }),
                },
                {
                  width: "15%",
                  title: intl.formatMessage({
                    id: "select_model.table_header_user",
                    defaultMessage: "User",
                  }),
                },
                {
                  width: "15%",
                  title: intl.formatMessage({
                    id: "select_model.table_header_organisation",
                    defaultMessage: "Organisation",
                  }),
                },
                {
                  width: "15%",
                  title: intl.formatMessage({
                    id: "select_model.table_header_created",
                    defaultMessage: "Created",
                  }),
                },
                {
                  width: "10%",
                  title: intl.formatMessage({
                    id: "selected_model.table_header_progress",
                    defaultMessage: "Progress",
                  }),
                },
              ]}
              tableDataIds={simulations.map((simulation) => "" + simulation.uid)}
              emptyElement={
                <tr>
                  <td colSpan={5}>
                    <Text>No simulations to show</Text>
                  </td>
                </tr>
              }
            >
              <RanaTable.Filters
                search={{
                  w: 400,
                  searchString: searchStringSimulation,
                  setSearchString: setSearchStringSimulation,
                  placeholder: intl.formatMessage({
                    id: "select_model.search_model_placeholder_follow_session",
                    defaultMessage: "Search and select a simulation to follow",
                  }),
                }}
              />
              <RanaTable.Container minH={(44 + 2) * 6}>
                <RanaTable.Header />
                <RanaTable.Body>
                  {simulations.map((simulation) => (
                    <RanaTable.Row
                      key={"" + simulation.uid}
                      rowId={"" + simulation.uid}
                      onClick={() => setSelectedSimulation(simulation)}
                      isSelected={!!selectedSimulation && selectedSimulation.uid === simulation.uid}
                    >
                      <RanaTable.Cell>{simulation.name}</RanaTable.Cell>
                      <RanaTable.Cell>
                        {simulation.user_name ? simulation.user_name : "---"}
                      </RanaTable.Cell>
                      <RanaTable.Cell>{simulation.organisation_name}</RanaTable.Cell>
                      <RanaTable.Cell>
                        {simulation.date_created
                          ? new Date(simulation.date_created).toLocaleDateString()
                          : "---"}
                      </RanaTable.Cell>
                      <RanaTable.Cell>{simulation.progress}</RanaTable.Cell>
                    </RanaTable.Row>
                  ))}
                </RanaTable.Body>
              </RanaTable.Container>
              <RanaTable.Pagination
                limit={SCENARIOS_PER_PAGE}
                page={pageSimulation}
                numPages={totalPagesSimulation}
                setPage={setPageSimulation}
              />
            </RanaTable>
          </>
        </RanaModal>
      );
    }

    return null;
  };

  if (organisations.length === 0) {
    return <SystemStatusPage variant="no-subscription" />;
  }

  return renderModal();
}

export default SelectScenario;
